/**
 * Custom product analytics — customProduct:* from orchestrator + upload widget selected/error.
 * Gated by init({ analytics: true|false }) → ready.config.analytics / getConfig().analytics.
 * Does not drive the pipeline. Requires analytics-module.js.
 *
 * Assets written (same as history — one of each for now):
 *   mockup   → endMockup(previewWithBg)  — same as ATC / history
 *   original → endSave(url) / endProcess(fileUrl) — primary processed s3Url
 * Multi-role saveSuccess.urls and saveUrl2 are ignored / not sent.
 *
 * Lifecycle: ready | uploadSelected | uploadError | init | propsInit | started
 *            | mockupStart | mockupSuccess | saveStart | saveSuccess
 *            | success | error
 * + PUB_SUB addedToCart
 *
 * start() always sends generationType = config.productType (e.g. shaped).
 * processType = full | history | share | color ← pipelineSource
 * tracksMockupTiming: only upload (start + timed end)
 * props: no startMockup; endMockup(..., colorEvent)
 * history | share: no start/end mockup
 * skipMockup on endProcess ← imageTaskSlim.status.mockup === 'skip'
 * Event imageTaskSlim is snapshot (no pillowData); getImageTask() is live full task.
 */
(() => {
  'use strict';

  const EVENTS = {
    READY: 'customProduct:ready',
    UPLOAD_SELECTED: 'customProduct:uploadSelected',
    UPLOAD_ERROR: 'customProduct:uploadError',
    INIT: 'customProduct:init',
    PROPS_INIT: 'customProduct:propsInit',
    STARTED: 'customProduct:started',
    MOCKUP_START: 'customProduct:mockupStart',
    MOCKUP_SUCCESS: 'customProduct:mockupSuccess',
    SAVE_START: 'customProduct:saveStart',
    SAVE_SUCCESS: 'customProduct:saveSuccess',
    SUCCESS: 'customProduct:success',
    ERROR: 'customProduct:error'
  };

  /** Values expected by AnalyticsModule (props change → processType "color") */
  const PROCESS_TYPE = {
    FULL: 'full',
    HISTORY: 'history',
    SHARE: 'share',
    PROPS: 'color'
  };

  let moduleReady = false;
  let processActive = false;
  let startedGeneration = -1;
  /** From ready.config / getConfig(); default true until ready says otherwise */
  let analyticsEnabled = true;

  const orchConfig = () => window.CustomProductOrchestrator?.getConfig?.() || null;

  const detailOf = (event) => event?.detail || {};

  const pipelineSourceOf = (detail) => detail?.pipelineSource || null;

  const isEnabled = () => {
    const config = orchConfig();
    if (config && typeof config.analytics === 'boolean') return config.analytics;
    return analyticsEnabled;
  };

  const formId = () => orchConfig()?.formId || 'product-panel-form';

  /** Maps to AnalyticsModule.start generationType (e.g. shaped; AI twin uses coloring). */
  const productType = () => orchConfig()?.productType || null;

  const formMeta = () => {
    const panelFormId = formId();
    const ProductFormCustom = window.ProductFormCustom;
    return {
      template: ProductFormCustom?.getCurrentTemplate?.(panelFormId) || '',
      product: ProductFormCustom?.getProductTitle?.(panelFormId) || '',
      productSku: ProductFormCustom?.getCurrentSku?.(panelFormId) || ''
    };
  };

  const initModule = () => {
    if (!isEnabled()) return false;
    if (moduleReady || !window.AnalyticsModule?.create) return false;

    const meta = formMeta();
    const config = {
      templateName: $('[data-analytics-template]').attr('data-analytics-template') || '',
      productTitle: meta.product
        || $('[data-analytics-product-title]').attr('data-analytics-product-title')
        || '',
      getProductSku: () => formMeta().productSku,
      getAccessStatus: () => (
        typeof window.accessCheck?.status === 'function'
          ? window.accessCheck.status()
          : true
      )
    };

    window.AnalyticsModule.create(config);
    window.AnalyticsModule.setLogging?.(config.getAccessStatus());
    moduleReady = true;
    return true;
  };

  const startProcess = (processType, extra = {}) => {
    if (!isEnabled() || !window.AnalyticsModule?.start) return;
    initModule();
    const meta = formMeta();
    window.AnalyticsModule.start({
      template: meta.template,
      product: meta.product,
      productSku: meta.productSku,
      processType,
      generationType: productType(),
      ...extra
    });
    processActive = true;
  };

  document.addEventListener(EVENTS.READY, (event) => {
    const detail = detailOf(event);
    if (typeof detail.config?.analytics === 'boolean') {
      analyticsEnabled = detail.config.analytics;
    }
    if (!isEnabled()) return;
    initModule();
  });

  // Upload widget (not via wiring) — start / fail before pipeline
  $(document).on('imageUploadInput:selected.customProductAnalytics', (event, payload) => {
    if (!isEnabled()) return;
    const data = payload || {};
    const orch = window.CustomProductOrchestrator;
    if (orch?.matchesUploadTarget?.(data.target) === null) return;
    const file = data.file || null;
    startProcess(PROCESS_TYPE.FULL, {
      imageType: file?.type || null
    });
  });

  $(document).on('imageUploadInput:error.customProductAnalytics', (event, payload) => {
    if (!isEnabled()) return;
    const data = payload || {};
    const orch = window.CustomProductOrchestrator;
    if (orch?.matchesUploadTarget?.(data.target) === null) return;
    initModule();
    window.AnalyticsModule?.endProcess?.('UNKNOWN', {
      error: {
        type: 'UNKNOWN',
        message: data.error?.message || data.message || 'Unknown error'
      }
    });
    processActive = false;
  });

  document.addEventListener(EVENTS.PROPS_INIT, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    startProcess(PROCESS_TYPE.PROPS);
    startedGeneration = detail.generation ?? startedGeneration;
  });

  document.addEventListener(EVENTS.INIT, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    const pipelineSource = pipelineSourceOf(detail);
    if (pipelineSource === 'history') {
      startProcess(PROCESS_TYPE.HISTORY, {
        imageType: detail.file?.type || 'image/jpeg'
      });
      startedGeneration = detail.generation ?? startedGeneration;
      return;
    }
    if (pipelineSource === 'share') {
      startProcess(PROCESS_TYPE.SHARE, {
        imageType: detail.file?.type || 'image/jpeg'
      });
      startedGeneration = detail.generation ?? startedGeneration;
    }
  });

  document.addEventListener(EVENTS.STARTED, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    const pipelineSource = pipelineSourceOf(detail);
    if (pipelineSource === 'history' || pipelineSource === 'props' || pipelineSource === 'share') {
      startedGeneration = detail.generation ?? startedGeneration;
      return;
    }
    if (processActive && pipelineSource === 'upload') {
      startedGeneration = detail.generation ?? startedGeneration;
      return;
    }
    if (processActive && detail.generation != null && detail.generation === startedGeneration) {
      return;
    }
    startProcess(PROCESS_TYPE.FULL, {
      imageType: detail.file?.type || 'image/jpeg'
    });
    startedGeneration = detail.generation ?? startedGeneration;
  });

  /** Timed mockup start/end — upload only (not props / history / share). */
  const tracksMockupTiming = (pipelineSource) => pipelineSource === 'upload';

  document.addEventListener(EVENTS.MOCKUP_START, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    if (!tracksMockupTiming(pipelineSourceOf(detail))) return;
    window.AnalyticsModule?.startMockup?.();
  });

  document.addEventListener(EVENTS.MOCKUP_SUCCESS, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    const pipelineSource = pipelineSourceOf(detail);
    if (!tracksMockupTiming(pipelineSource) && pipelineSource !== 'props') return;
    const task = window.CustomProductOrchestrator?.getImageTask?.();
    window.AnalyticsModule?.endMockup?.(
      task?.previewWithBg || task?.preview || null,
      pipelineSource === 'props'
    );
  });

  document.addEventListener(EVENTS.SAVE_START, () => {
    if (!isEnabled()) return;
    setTimeout(() => {
      if (!isEnabled()) return;
      window.AnalyticsModule?.startSave?.();
    }, 100);
  });

  document.addEventListener(EVENTS.SAVE_SUCCESS, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    if (detail.skipped) return;
    window.AnalyticsModule?.endSave?.(detail.imageTaskSlim?.s3Url || detail.url || null);
  });

  document.addEventListener(EVENTS.SUCCESS, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    const task = detail.imageTaskSlim;
    if (!task || !window.AnalyticsModule?.endProcess) return;
    window.AnalyticsModule.endProcess('OK', {
      fileUrl: task.s3Url || null,
      skipSave: task.status?.saveToS3 === 'skip',
      skipMockup: task.status?.mockup === 'skip'
    });
    processActive = false;
  });

  document.addEventListener(EVENTS.ERROR, (event) => {
    if (!isEnabled()) return;
    const detail = detailOf(event);
    const task = detail.imageTaskSlim;
    if (!window.AnalyticsModule?.endProcess) return;
    window.AnalyticsModule.endProcess(detail.errorType || 'unknown', {
      error: task?.error || null,
      skipSave: task?.status?.saveToS3 === 'skip',
      skipMockup: task?.status?.mockup === 'skip'
    });
    processActive = false;
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.addedToCart, () => {
      if (!isEnabled()) return;
      window.AnalyticsModule?.toCart?.(formMeta().productSku);
    });
  }

  if (window.customProductOrchestratorInitialized) {
    const config = orchConfig();
    if (typeof config?.analytics === 'boolean') analyticsEnabled = config.analytics;
    if (isEnabled()) initModule();
  }
})();
